import type { DemoMemoryPreset } from '@timelyapp/integration-cloud-sdk/memory-demo-data/types';

/**
 * Every demo person's id is `DEMO_ID_START + <roster index>`, assigned by the
 * procedural generator (`usersPage.demo.generator.ts`). Ids sit far above any
 * real user id so a workspace member can never be mistaken for a demo person
 * while a preset is on.
 */
export const DEMO_ID_START = 900000001;

/** Fixed seed for the procedural generator, so a given size always produces
 *  byte-identical output. Workspace-derived seeding is a follow-up. */
export const DEMO_GENERATOR_SEED = 900000000;

/** Default roster size for the procedurally generated variant. */
export const DEMO_TEAM_SIZE = 25;

/**
 * How many fictional pending invites sit in the Invited tab. Separate from
 * DEMO_TEAM_SIZE: these people are never part of the active roster (no hours,
 * no team, no compliance/activity data) — they only exist as rows in the
 * Invited tab, ids continuing right after the active roster's range.
 */
export const DEMO_INVITED_COUNT = 5;

/**
 * Per-preset team size overrides, keyed by the same preset enum the memory
 * demo data already uses. Structurally present so each preset can carry its
 * own size later; only the default wires through for now.
 */
export const DEMO_TEAM_SIZE_BY_PRESET: Partial<Record<DemoMemoryPreset, number>> = {};

export function getDemoTeamSizeForPreset(preset: DemoMemoryPreset | undefined): number {
  return (preset && DEMO_TEAM_SIZE_BY_PRESET[preset]) ?? DEMO_TEAM_SIZE;
}

/**
 * The generated variant's demo ids are `DEMO_ID_START + index` by
 * construction, so the id set is pure arithmetic — computing it never needs
 * to run the seeded roster generator (and its faker calls). Kept here,
 * dependency-free, so a cheap "is this a demo id" check never has to pull in
 * generation machinery just to answer itself.
 */
export function demoIdRange(size: number, offset = 0): ReadonlySet<number> {
  return new Set(Array.from({ length: size }, (_, i) => DEMO_ID_START + offset + i));
}

/**
 * One past the last id any demo person (active or invited) can have —
 * exclusive, so `id < DEMO_ID_END` reads naturally. Same rationale as
 * demoIdRange: pure arithmetic, no need to touch the generator.
 */
export const DEMO_ID_END = DEMO_ID_START + DEMO_TEAM_SIZE + DEMO_INVITED_COUNT;

/**
 * Whether `id` belongs to the generated demo roster (active or invited) at
 * all — independent of whether the preset is currently on. Thunks that act
 * on a specific user id (edit/delete/re-invite) must check this, not just
 * "is the preset active": the preset being on elsewhere doesn't make a real
 * account's id a demo id, and blocking real ids by mistake silently breaks
 * real actions (TT-5152 review).
 */
export function isDemoUserId(id: number): boolean {
  return id >= DEMO_ID_START && id < DEMO_ID_END;
}
